// BulkPackingSheets.jsx — print several packing sheets in one go (Brian,
// 2026-10-13: "instead of having to go click into every single packing list").
// Opened from the Orders page with the ticked orders: /packing-bulk?ids=a,b,c&print=1
// Each order renders as one PackingSheetView; in print each sheet starts on a
// new page (.packing-bulk rules in index.css). With print=1 the browser's
// print dialog opens by itself once everything has loaded.

import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { db } from "../config/firebase";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { getAllProducts, getCustomerRecords } from "../services/dataCache";
import PackingSheetView, { preparePackingSheet } from "../components/PackingSheetView";

export default function BulkPackingSheets() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const ids = (params.get("ids") || "").split(",").map(s => s.trim()).filter(Boolean);
  const autoPrint = params.get("print") === "1";

  const [orders, setOrders] = useState([]);
  const [notFound, setNotFound] = useState([]);
  const [names, setNames] = useState({});          // order id -> customer display name
  const [productsBySku, setProductsBySku] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        if (ids.length === 0) {
          setError("No orders selected. Go back to Orders, tick the ones you want, and press Print packing lists.");
          return;
        }
        // Products + customer records come from the session cache (0 reads on
        // revisit); the users read resolves self-placed orders and is guarded
        // because reps may not be allowed to read other users' docs.
        const [prods, recs] = await Promise.all([getAllProducts(), getCustomerRecords()]);
        setProductsBySku(Object.fromEntries(prods.map(p => [p.sku, p])));
        const recById = Object.fromEntries(recs.map(r => [r.id, r]));
        let users = {};
        try {
          const us = await getDocs(collection(db, "users"));
          users = Object.fromEntries(us.docs.map(d => [d.id, d.data()]));
        } catch {
          // no permission — names for self-placed orders fall back to "Unknown customer"
        }

        const snaps = await Promise.all(ids.map(id => getDoc(doc(db, "orders", id))));
        const found = [];
        const missing = [];
        snaps.forEach((s, i) => {
          if (s.exists()) found.push({ id: s.id, ...s.data() });
          else missing.push(ids[i]);
        });

        const n = {};
        for (const o of found) {
          const rec = o.customerRecordId ? recById[o.customerRecordId] : null;
          const u = !rec && o.customerId ? users[o.customerId] : null;
          n[o.id] = rec
            ? (rec.name || rec.qbCustomerName || "Unknown")
            : u ? (u.companyName || u.name || u.email || "Unknown")
            : "Unknown customer";
        }
        setOrders(found);
        setNotFound(missing);
        setNames(n);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [search]); // the id list lives in the URL; re-run only when it changes

  // Auto-open the print dialog once the sheets are on screen (print=1).
  useEffect(() => {
    if (loading || error || !autoPrint || orders.length === 0) return undefined;
    const t = setTimeout(() => window.print(), 400);
    return () => clearTimeout(t);
  }, [loading, error, autoPrint, orders.length]);

  if (loading) return <p>Loading packing sheets...</p>;
  if (error) {
    return (
      <div className="packing-page">
        <div className="no-print packing-toolbar">
          <Link to="/admin/orders" className="clear-selection-btn">← Back to Orders</Link>
        </div>
        <p className="error">{error}</p>
      </div>
    );
  }

  // One combined storage warning for the batch (on screen only).
  const warnings = orders
    .map(o => ({ o, missing: preparePackingSheet(o, productsBySku).missingStorageSkus }))
    .filter(w => w.missing.length > 0);

  return (
    <div className="packing-page">
      <div className="no-print packing-toolbar">
        <Link to="/admin/orders" className="clear-selection-btn">← Back to Orders</Link>
        <button className="print-btn" onClick={() => window.print()}>
          🖨 Print {orders.length} packing sheet{orders.length === 1 ? "" : "s"} (Ctrl+P)
        </button>
        <span className="packing-bulk-hint">Each order prints on its own page.</span>
      </div>

      {notFound.length > 0 && (
        <div className="no-print storage-warning-banner">
          ⚠ {notFound.length} selected order{notFound.length === 1 ? "" : "s"} could not be found and
          {notFound.length === 1 ? " was" : " were"} skipped.
        </div>
      )}
      {warnings.length > 0 && (
        <div className="no-print storage-warning-banner">
          ⚠ SKUs with no storage method (sorted last on their sheet):{" "}
          {warnings.map(w => `${w.o.orderNumber}: ${w.missing.join(", ")}`).join(" · ")}.
          Set their storage method on the Products page so they group correctly.
        </div>
      )}

      <div className="packing-bulk">
        {orders.map(o => (
          <PackingSheetView key={o.id} order={o} customerName={names[o.id]} productsBySku={productsBySku} />
        ))}
      </div>
    </div>
  );
}
